import { HttpService } from "@services/http";
import {
  prepareErrorResponse,
  prepareResponseObject,
} from "@services/http/response";
import { RESPONSE_TYPES } from "@constants/response-types";
import { getBaseUrl } from "@root/store/selectors/features";
import { store } from "@root/store";
import { uploadFileInChunks } from "@utils/common";

/**
 * Document Service API signatures
 * - createDocument: POST /api/documents/upload-chunk (chunked upload)
 * - createDocumentWithProgress: POST /api/documents/upload-chunk with progress tracking (chunked upload)
 * - getAllDocuments: GET /api/documents, optional form-data (file)
 * - getDocumentById: GET /api/documents/:id
 * - processDocument: POST /api/documents/upload-to-ai, { id }
 */

// ==================== Document Service ====================

export class DocumentsService extends HttpService {
  baseUrl = getBaseUrl(store.getState());

  /**
   * Upload document in chunks
   * POST /api/documents/upload-chunk
   */
  createDocument = async (
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<TObject> => {
    try {
      // Extract file from FormData
      const file = formData.get("file") as File;

      if (!file) {
        throw new Error("No file found in FormData");
      }

      // Get auth headers
      const headers = await this.getHeaders({});

      // Upload file in chunks using utility function
      const responseData = await uploadFileInChunks(file, formData, {
        headers,
        baseUrl: this.baseUrl,
        endpoint: "/api/documents/upload-chunk",
        timeout: this.getTimeOutDuration(),
        onProgress,
      });

      console.log("Final upload response data:", responseData);
      return prepareResponseObject(
        { data: { data: responseData } },
        RESPONSE_TYPES.SUCCESS
      );
    } catch (error) {
      console.error("Error in createDocument:", error);
      return prepareErrorResponse(error);
    }
  };

  /**
   * Upload document in chunks with progress tracking and abort signal support
   * POST /api/documents/upload-chunk
   */
  createDocumentWithProgress = async (
    formData: FormData,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal
  ): Promise<TObject> => {
    try {
      // Extract file from FormData
      const file = formData.get("file") as File;

      if (!file) {
        throw new Error("No file found in FormData");
      }

      // Get auth headers
      const headers = await this.getHeaders({});

      // Upload file in chunks using utility function
      const responseData = await uploadFileInChunks(file, formData, {
        headers,
        baseUrl: this.baseUrl,
        endpoint: "/api/documents/upload-chunk",
        timeout: this.getTimeOutDuration(),
        onProgress,
        signal,
      });

      return prepareResponseObject(
        { data: { data: responseData } },
        RESPONSE_TYPES.SUCCESS
      );
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getAllDocuments = async (payload?: any): Promise<TObject> => {
    try {
      let apiResponse;
      apiResponse = await this.get(`${this.baseUrl}/api/documents`, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getDocumentById = async (id: string | number): Promise<TObject> => {
    try {
      const apiResponse = await this.get(`${this.baseUrl}/api/documents/${id}`);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  processDocument = async (payload: {
    id: string | number;
  }): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/documents/upload-to-ai`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  processDocumentReview = async (payload: {
    id: string | number;
  }): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/documents/upload-to-ai-review`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  processDocumentLegal = async (payload: {
    id: string | number;
  }): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/documents/excel-auto-complete`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  processMaterialComparison = async (payload: any): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/documents/upload-to-ai-finishing-material-request`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getSuggestedQuestions = async (payload?: any): Promise<TObject> => {
    try {
      let apiResponse;
      apiResponse = await this.get(
        `${this.baseUrl}/api/suggestion-questions`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getFaqs = async (payload?: any): Promise<TObject> => {
    try {
      let apiResponse;
      apiResponse = await this.get(`${this.baseUrl}/api/faqs`, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getChats = async (payload?: any): Promise<TObject> => {
    try {
      const apiResponse = await this.get(`${this.baseUrl}/api/chats`, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  likeChat = async (payload?: any): Promise<TObject> => {
    try {
      const { chatId } = payload;
      const apiResponse = await this.patch(
        `${this.baseUrl}/api/chat/${chatId}/like`,
        { is_liked: payload.is_liked }
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  createExtractionReviews = async (payload: {
    fileIds: number[];
  }): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/extraction-reviews`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getExtractionReviews = async (payload?: any): Promise<TObject> => {
    try {
      const apiResponse = await this.get(
        `${this.baseUrl}/api/extraction-reviews`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  callExtractionReviewsAI = async (payload: {
    id: string | number;
  }): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/extraction-reviews/call-ai`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getExtractionReviewById = async (id: string | number): Promise<TObject> => {
    try {
      const apiResponse = await this.get(
        `${this.baseUrl}/api/extraction-reviews/${id}`
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  deleteExtractionReview = async (id: string | number): Promise<TObject> => {
    try {
      const apiResponse = await this.delete(
        `${this.baseUrl}/api/extraction-reviews/${id}`
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  /**
   * Retry failed document processing
   * POST /api/documents/retry-doc
   */
  retryDocument = async (payload: {
    project_id: number;
    fileName_key: string;
    fileName: string;
    id: number;
  }): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/documents/retry-doc`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };
}
